// Intake form question schema
export const QUESTIONS = [
  {
    key: "is_texas",
    label: "Step 1 of 7",
    text: "Is your dispute based in Texas or does it involve a Texas party?",
    options: [
      { value: "yes", label: "Yes — Texas-based dispute or Texas party involved" },
      { value: "no",  label: "No — outside Texas entirely" },
    ],
  },
  {
    key: "matter_type",
    label: "Step 2 of 7",
    text: "What best describes your legal matter?",
    options: [
      "Contract dispute",
      "Fraud or misrepresentation",
      "Partner, shareholder, or co-founder conflict",
      "Securities or investment dispute",
      "Energy, oil & gas, or mineral rights dispute",
      "Complex real estate or development dispute",
      "Employment litigation",
      "Business acquisition or M&A matter",
      "Other business dispute",
    ].map((v) => ({ value: v, label: v })),
  },
  {
    key: "damages",
    label: "Step 3 of 7",
    text: "How would you estimate the financial impact of this dispute on your business?",
    sub: "This helps us match you with firms that handle matters at the right scale.",
    options: [
      "Under $100,000",
      "$100,000 – $500,000",
      "$500,000 – $2,000,000",
      "More than $2,000,000",
      "Still assessing — not yet quantified",
    ].map((v) => ({ value: v, label: v })),
  },
  {
    key: "evidence",
    label: "Step 4 of 7",
    text: "What documentation do you have supporting your position?",
    options: [
      "Written contract, signed agreements, or formal documentation",
      "Emails, messages, financial records — partial documentation",
      "Mostly verbal — limited written records",
      "I'm in the process of gathering documentation",
    ].map((v) => ({ value: v, label: v })),
  },
  {
    key: "urgency",
    label: "Step 5 of 7",
    text: "Is there a time-sensitive element to your matter?",
    options: [
      "Yes — urgent, deadline within 30 days or emergency relief needed",
      "Yes — I need to act within the next few months",
      "No immediate deadline — but I want to move forward",
      "Unknown — I need an attorney to advise on deadlines",
    ].map((v) => ({ value: v, label: v })),
  },
  {
    key: "budget",
    label: "Step 6 of 7",
    text: "What are your expectations for legal fees to pursue this matter?",
    sub: "Different firms structure fees differently. Knowing your expectations helps us match you with a firm whose fee arrangement fits your situation.",
    options: [
      "I am looking for a free consultation or minimal upfront cost",
      "I can commit a modest engagement fee — up to $10,000",
      "I am prepared to invest $10,000 – $50,000 to pursue this matter",
      "I understand complex litigation requires significant investment",
      "I am looking for a contingency or no-win-no-fee arrangement",
    ].map((v) => ({ value: v, label: v })),
  },
];

export const PRACTICE_AREAS = [
  { title: "Contract Disputes", body: "Breach of supply agreements, service contracts, construction contracts, technology agreements, and distribution deals." },
  { title: "Business Fraud & Misrepresentation", body: "Fraudulent inducement, material misrepresentation in business deals, and deceptive trade practices." },
  { title: "Partner & Shareholder Disputes", body: "Breach of fiduciary duty, partner diversion, minority shareholder oppression, operating agreement violations, and forced buyout disputes." },
  { title: "Securities Litigation", body: "State securities violations, private placement misrepresentation, broker-dealer misconduct, and federal securities fraud claims in Texas federal courts." },
  { title: "Energy & Oil and Gas", body: "Joint operating agreement disputes, royalty underpayment, mineral rights conflicts, pipeline and midstream claims, and exploration contract litigation across the Permian, Eagle Ford, and Haynesville." },
  { title: "Trade Secrets & IP", body: "Trade secret misappropriation, non-compete enforcement, customer list theft, proprietary process protection, and tortious interference with business relations. Matters are handled with strict confidentiality protocols, including protective orders and sealed filings where appropriate." },
  { title: "Employment Litigation", body: "Executive compensation disputes, wrongful termination, severance and non-compete enforcement, discrimination and retaliation claims, and trade-secret crossover matters affecting senior employees and the businesses that employ them." },
  { title: "Business Acquisition", body: "Strategic counsel for founders and operators preparing to grow with private-equity capital or pursue an exit: roll-up and platform deal structuring, capital raises, sell-side and buy-side M&A, recapitalizations, earn-out negotiation, and post-closing transition planning." },
];

export const STEPS = [
  { n: "1", title: "Complete the confidential intake assessment", body: "Describe your matter through our structured questionnaire. The AI layer analyzes your dispute type, damages profile, evidence strength, and urgency against Texas litigation standards.", tag: "Takes 3 minutes · Completely confidential" },
  { n: "2", title: "We assess and identify your best firm match", body: "A licensed Texas attorney reviews your assessment alongside our network of firms — matching practice area, Texas jurisdiction, matter complexity, and the specific legal theories your case requires.", tag: "Up to 72 hours depending on facts" },
  { n: "3", title: "You're introduced to the matched firm", body: "We make a direct introduction to the appropriate practice group at the matched firm — with a summary of your matter so they arrive prepared, not starting from zero.", tag: "No cold calls. A warm, informed introduction." },
  { n: "4", title: "You decide — no obligation", body: "The introduction is yours to act on. You speak with the firm, review their engagement terms, and decide whether to retain them.", tag: "Your decision, your timeline" },
];

export const EXAMPLES = [
  { type: "Contract Dispute", scenario: "A Houston manufacturer paid $900,000 under a signed supply agreement. The vendor delivered defective components for six months, causing three major client contract losses. Written contract, delivery records, and client loss documentation all available.", checks: ["Written contract — fee-shifting available","Quantifiable damages — $900K+ direct + consequential","Strong documentary evidence","Match: Complex commercial litigation firm, Houston"] },
  { type: "Securities Dispute", scenario: "A Dallas family office invested $3.5M in a private placement based on materially false revenue projections. Funds were partially misappropriated. Offering documents, subscription agreement, and bank records documenting the misuse are all available.", checks: ["State and federal securities theories available","Written offering memo — documented misrepresentation","$3.5M damages — securities litigation firm required","Match: Securities litigation practice, Dallas"] },
  { type: "Energy / JOA Dispute", scenario: "An E&P company operating in the Permian Basin discovered its joint venture partner has diverted $2.8M in production revenues over 14 months in violation of their joint operating agreement. Forensic accountant has produced a preliminary report.", checks: ["Signed operating agreement — clear breach, fee-shifting available","Forensic accounting evidence — strong damages","Operating company defendant — solvent","Match: Energy litigation group, Dallas or Houston"] },
];
